/**
 * Answer utility functions for LiveSessionInterface
 */

/**
 * Check if an answer object has any value
 * @param {Object} answer - Answer object
 * @returns {boolean} True if answer has any value
 */
export const isAnswerValid = (answer) => {
  if (!answer) return false;
  
  return (
    (answer.answer && answer.answer.trim() !== '') ||
    answer.answer_numeric !== null && answer.answer_numeric !== undefined ||
    answer.answer_boolean !== null && answer.answer_boolean !== undefined
  );
};

/**
 * Calculate the number of answered questions
 * @param {Object} currentAnswers - Object mapping question IDs to answers
 * @returns {number} Count of answered questions
 */
export const calculateAnsweredCount = (currentAnswers) => {
  if (!currentAnswers || typeof currentAnswers !== 'object') {
    return 0;
  }
  
  return Object.keys(currentAnswers).filter(questionId => {
    return isAnswerValid(currentAnswers[questionId]);
  }).length;
};

/**
 * Calculate progress percentage
 * @param {number} answeredCount - Number of answered questions
 * @param {number} totalQuestions - Total number of questions
 * @returns {number} Progress percentage (0-100)
 */
export const calculateProgressPercent = (answeredCount, totalQuestions) => {
  if (!totalQuestions || totalQuestions === 0) {
    return 0;
  }
  return Math.round((answeredCount / totalQuestions) * 100);
};

/**
 * Parse metadata answers from assessment and convert to answers map
 * @param {Object|string} metadata - Assessment metadata (can be string or object)
 * @returns {Object} Map of question_id to answer objects
 */
export const parseMetadataAnswers = (metadata) => {
  if (!metadata) return {};
  
  // Parse if string
  const parsedMetadata = typeof metadata === 'string' 
    ? JSON.parse(metadata)
    : metadata;
  
  if (!parsedMetadata.answers || !Array.isArray(parsedMetadata.answers)) {
    return {};
  }
  
  const answersMap = {};
  parsedMetadata.answers.forEach(answer => {
    answersMap[answer.question_id] = {
      question_id: answer.question_id,
      answer: answer.answer,
      answer_numeric: answer.answer_numeric,
      answer_boolean: answer.answer_boolean,
      answer_json: answer.answer_json,
      notes: answer.additional_info
    };
  });
  
  return answersMap;
};

/**
 * Format answer value for display
 * @param {Object} answer - Answer object
 * @returns {string} Formatted answer string
 */
export const formatAnswerForDisplay = (answer) => {
  if (!answer) return '';
  
  if (answer.answer && answer.answer.trim() !== '') {
    return answer.answer;
  }
  
  if (answer.answer_numeric !== null && answer.answer_numeric !== undefined) {
    return String(answer.answer_numeric);
  }
  
  if (answer.answer_boolean !== null && answer.answer_boolean !== undefined) {
    return answer.answer_boolean ? 'Yes' : 'No';
  }
  
  if (answer.answer_json) {
    return 'Complex answer';
  }
  
  return '';
};

